import { WASH_PROGRAM_KIND_LABELS, WASH_PROGRAM_KINDS, type WashProgram } from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Pencil, Plus, SprayCan } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ProgramDialog } from "../../../features/wash/program-dialog.js";
import {
  useAllWashUnitNames,
  useWashPermissions,
  useWashPrograms,
  useWashStations,
} from "../../../features/wash/queries.js";
import { WashPage } from "../../../features/wash/shared.js";
import { formatDuration } from "../../../features/wash/wash-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { formatCount } from "../../../lib/format.js";

const searchSchema = z.object({
  kind: z.enum(WASH_PROGRAM_KINDS).optional().catch(undefined),
  active: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/yikama/programlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ProgramsRoute,
});

/**
 * Yıkama programları (Prompt 18 web, ADR 0018 §5): kiracı kataloğu — tür, satış ürünü (fiyat ürünün istasyon fiyatıdır),
 * süre, tahmini su / elektrik, kimyasal reçetesi ve programı sunan üniteler. Yazma `wash.manage`.
 */
function ProgramsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useWashPermissions();
  const [dialog, setDialog] = useState<{ program: WashProgram | null } | null>(null);
  return (
    <WashPage
      title={t("menu.washPrograms")}
      description={t("wash.programs.description")}
      actions={
        permissions.canManage ? (
          <Button onClick={() => setDialog({ program: null })}>
            <Plus aria-hidden="true" />
            {t("wash.programs.add")}
          </Button>
        ) : null
      }
    >
      <ProgramsBody dialog={dialog} setDialog={setDialog} />
    </WashPage>
  );
}

function ProgramsBody({
  dialog,
  setDialog,
}: {
  dialog: { program: WashProgram | null } | null;
  setDialog: (next: { program: WashProgram | null } | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useWashPermissions();
  const programs = useWashPrograms();
  const stations = useWashStations();
  const unitNames = useAllWashUnitNames(stations.data ?? []);
  const setSearch = (patch: Record<string, string | undefined>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => ({ ...previous, ...patch })) as never,
      replace: true,
    });

  const rows = (programs.data ?? []).filter(
    (program) =>
      (search.kind === undefined || program.kind === search.kind) &&
      (search.active === undefined || String(program.isActive) === search.active),
  );

  const columns = useMemo<DataTableColumn<WashProgram>[]>(
    () => [
      {
        id: "code",
        header: t("wash.fields.code"),
        hideable: false,
        exportValue: (row) => row.code,
        cell: (row) => <span className="font-mono font-medium whitespace-nowrap">{row.code}</span>,
      },
      { id: "name", header: t("wash.fields.name"), exportValue: (row) => row.name, cell: (row) => row.name },
      {
        id: "kind",
        header: t("wash.fields.kind"),
        exportValue: (row) => WASH_PROGRAM_KIND_LABELS[row.kind],
        cell: (row) => WASH_PROGRAM_KIND_LABELS[row.kind],
      },
      {
        id: "product",
        header: t("wash.fields.product"),
        exportValue: (row) => `${row.productCode} ${row.productName}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="font-mono text-xs">{row.productCode}</span>
            <span>{row.productName}</span>
          </span>
        ),
      },
      {
        id: "duration",
        header: t("wash.fields.duration"),
        align: "right",
        exportValue: (row) => row.durationSeconds,
        cell: (row) =>
          row.kind === "SELF_SERVICE" ? (
            <span className="text-muted-foreground">{t("wash.programs.perMinute")}</span>
          ) : (
            <span className="tabular whitespace-nowrap">{formatDuration(row.durationSeconds)}</span>
          ),
      },
      {
        id: "utilities",
        header: t("wash.programs.utilities"),
        exportValue: (row) => `${row.waterLitres} L · ${row.electricityKwh} kWh`,
        cell: (row) => (
          <span className="tabular whitespace-nowrap text-xs">
            {Decimal.from(row.waterLitres).formatTr(1)} L · {Decimal.from(row.electricityKwh).formatTr(2)} kWh
            {row.kind === "SELF_SERVICE" ? ` ${t("wash.programs.perMinuteShort")}` : ""}
          </span>
        ),
      },
      {
        id: "recipe",
        header: t("wash.programs.recipe"),
        exportValue: (row) =>
          row.consumptions.map((line) => `${line.productCode} ${line.quantity}`).join(", "),
        cell: (row) =>
          row.consumptions.length === 0 ? (
            <span className="text-muted-foreground">{t("wash.programs.noRecipe")}</span>
          ) : (
            <span className="grid gap-0.5 text-xs">
              {row.consumptions.map((line) => (
                <span key={line.productId}>
                  {line.productName}:{" "}
                  <span className="tabular">
                    {Decimal.from(line.quantity).formatTr(3).replace(/,?0+$/, "")}
                  </span>{" "}
                  {t(`enums.productUnit.${line.unit}`)}
                </span>
              ))}
            </span>
          ),
      },
      {
        id: "units",
        header: t("wash.programs.units"),
        exportValue: (row) => row.unitIds.map((id) => unitNames.get(id) ?? id).join(", "),
        cell: (row) =>
          row.unitIds.length === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="grid gap-0.5 text-xs">
              {row.unitIds.map((id) => (
                <span key={id}>{unitNames.get(id) ?? t("wash.programs.unknownUnit")}</span>
              ))}
            </span>
          ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => (row.isActive ? t("common.active") : t("common.inactive")),
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
      },
      ...(permissions.canManage
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              cell: (row: WashProgram) => (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t("wash.programs.editLabel", { code: row.code })}
                  onClick={() => setDialog({ program: row })}
                >
                  <Pencil aria-hidden="true" />
                  {t("common.edit")}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, unitNames, permissions.canManage, setDialog],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("wash.fields.kind")} className="w-60">
          {(control) => (
            <Select
              {...control}
              value={search.kind ?? ""}
              onChange={(event) => setSearch({ kind: event.target.value || undefined })}
            >
              <option value="">{t("common.all")}</option>
              {WASH_PROGRAM_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {WASH_PROGRAM_KIND_LABELS[kind]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("common.status")} className="w-40">
          {(control) => (
            <Select
              {...control}
              value={search.active ?? ""}
              onChange={(event) => setSearch({ active: event.target.value || undefined })}
            >
              <option value="">{t("common.all")}</option>
              <option value="true">{t("common.active")}</option>
              <option value="false">{t("common.inactive")}</option>
            </Select>
          )}
        </FormField>
        <p className="ms-auto text-sm text-muted-foreground">
          {t("wash.programs.count", { count: formatCount(rows.length) })}
        </p>
      </Card>
      <DataTable
        caption={t("wash.programs.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={programs.isPending}
        error={programs.error ? <Alert tone="danger">{errorMessage(programs.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<SprayCan />}
            title={t("wash.programs.emptyTitle")}
            description={t("wash.programs.emptyDescription")}
          />
        }
        onExport={async (visible) =>
          exportRowsToExcel({
            fileName: "yikama-programlari",
            sheetName: t("menu.washPrograms"),
            columns: visible,
            rows,
          })
        }
      />
      {dialog ? (
        <ProgramDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          program={dialog.program}
          unitNames={unitNames}
        />
      ) : null}
    </>
  );
}
